import React, { FocusEvent } from "react"
import { connect } from "react-redux"
import ContentEditable from "react-contenteditable"

import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { Field, Items } from "@tenforce/toolbox-field-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import bem from "../../../../bem"
import { DispatchProps, mapDispatchToProps } from "../../actions"
import { GlobalState } from "../../../../store"

import Suggestions from "./suggestions/Suggestions"
import Tutorial from "../components/tutorial/Tutorial"

import {
    generateHTML,
    getCaretPosition,
    getInitialHTML,
    setCaretPosition,
} from "../utils/helpers"
import {
    clearSuggestions,
    focusOnSuggestion,
    getFocusedSuggestion,
    handleSelectSuggestion,
    loadSuggestions,
    onKeyDownInput,
    onSuggestionClick,
} from "../utils/suggestions/SuggestionsHandler"
import { doSearch } from "../utils/search"
import { SearchIds } from "../../constants"
import { Labels } from "../../../../util/Translations"

const b = bem.with("NewUI__smartSearchField")

interface State {
    html: string
    focusedSuggestionCode: string | null
    suggestions: Search.Suggestion[]
    doingSearch: boolean
}

interface OwnProps {
    inModal?: boolean
    isInverted?: boolean
}

interface StateProps extends Search.Search {
    userFullName: string
}

type Props = OwnProps & StateProps & DispatchProps

export class SearchInputComponent extends React.Component<Props, State> {
    input: React.RefObject<HTMLDivElement>
    // ex: selecting a suggestion inserts text into input - caret needs to be shifted accordingly
    oneTimeCaretShiftAmount = 0

    constructor(props: Props) {
        super(props)
        this.input = React.createRef()

        const { normalizedNameLUT, workspaceId } = this.props
        const { html } = getInitialHTML(workspaceId, normalizedNameLUT)

        this.state = {
            html,
            suggestions: [] as Search.Suggestion[],
            focusedSuggestionCode: null,
            doingSearch: false,
        }
    }

    doSearch = doSearch.bind(this)
    onKeyDownInput = onKeyDownInput.bind(this)
    loadSuggestions = loadSuggestions.bind(this)
    clearSuggestions = clearSuggestions.bind(this)
    onSuggestionClick = onSuggestionClick.bind(this)
    focusOnSuggestion = focusOnSuggestion.bind(this)
    getFocusedSuggestion = getFocusedSuggestion.bind(this)
    handleSelectSuggestion = handleSelectSuggestion.bind(this)

    getInputText = (): string => {
        const element = this.input.current
        if (!element) {
            return ""
        }
        return element.textContent || ""
    }

    onEdit = (): void => {
        const inputFieldText = this.getInputText()
        const generated = generateHTML(
            inputFieldText,
            this.props.normalizedNameLUT,
        )
        this.oneTimeCaretShiftAmount =
            inputFieldText.length - generated.selectableLength
        this.setState({ html: generated.html })
        this.loadSuggestions()
    }
    onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (!!this.state.html) {
            this.onKeyDownInput(event)
        }
    }

    getSnapshotBeforeUpdate(): number | null {
        const element = this.input.current
        return getCaretPosition(element)
    }

    componentDidUpdate(
        prevProps: Props,
        prevState: State,
        snapshot: number | null,
    ): void {
        if (snapshot == null || prevProps !== this.props) {
            return
        }

        const offset = snapshot - this.oneTimeCaretShiftAmount
        this.oneTimeCaretShiftAmount = 0
        this.input.current && setCaretPosition(this.input.current, offset)
    }

    focusInput = ({ relatedTarget }: FocusEvent<HTMLDivElement>): void => {
        if (
            relatedTarget !== null &&
            (relatedTarget as HTMLElement).id !== SearchIds.showSearchButton
        ) {
            return
        }
        const input = this.input.current
        input && setCaretPosition(input, input.textContent?.length ?? 0)
    }

    blurInput = (): void => {
        this.clearSuggestions()
        window.getSelection()?.removeAllRanges()
    }

    render(): React.ReactNode {
        const isLoading = this.state.doingSearch
        const inModal = this.props.inModal ?? false
        const isInverted = this.props.isInverted ?? false

        const fieldClasses = `${b()}
            has-hidden-actions ${isLoading ? "is-loading" : ""} ${
            inModal ? "bg-darkest" : ""
        } ${isInverted ? "is-inverted" : ""}`

        const { Search, SearchThroughItems } = Labels
        return (
            <Field isInverted={isInverted} extraClasses={fieldClasses}>
                <Items extraClasses={"tw-inline-flex tw-w-full tw-pl-2"}>
                    <ContentEditable
                        id="searchInput"
                        data-ft-search-input
                        html={this.state.html}
                        onChange={this.onEdit}
                        innerRef={this.input}
                        onKeyDown={this.onKeyDown}
                        spellCheck={false}
                        className="toolbox-field__input"
                        onBlurCapture={this.blurInput}
                        onFocus={this.focusInput}
                        placeholder={SearchThroughItems}
                    />

                    <Tutorial
                        buttonExtraClasses={inModal ? "" : "is-hidden-action"}
                    />

                    <Action
                        id="doSearchButton"
                        data-ft-search-button
                        actionType={ActionType.icon}
                        extraClasses={inModal ? "" : "is-hidden-action"}
                        title={Search}
                        onClick={this.doSearch}
                        isDisabled={!this.state.html}
                    >
                        {Symbols.Search}
                    </Action>
                </Items>
                {this.state.suggestions.length > 0 && (
                    <Suggestions
                        inModal={inModal}
                        suggestions={this.state.suggestions}
                        onSuggestionClick={this.onSuggestionClick}
                        focusedSuggestionCode={this.state.focusedSuggestionCode}
                    />
                )}
            </Field>
        )
    }
}

const mapStateToProps = (state: GlobalState): StateProps => ({
    ...state.search,
    userFullName: state.userInfo.fullName,
})

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(SearchInputComponent)
